import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(date));
}

export function formatRelativeTime(date: string | Date): string {
  const now = new Date();
  const d = new Date(date);
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(date);
}

export function getInitials(name: string | null | undefined): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function getStageColor(stage: string): string {
  switch (stage) {
    case "lead_identified":
      return "var(--stage-lead)";
    case "contacted":
      return "var(--stage-contacted)";
    case "negotiating":
      return "var(--stage-negotiating)";
    case "won":
      return "var(--stage-won)";
    case "lost":
      return "var(--stage-lost)";
    default:
      return "var(--muted-foreground)";
  }
}

export function getStageBgClass(stage: string): string {
  switch (stage) {
    case "lead_identified":
      return "bg-blue-500/15 text-blue-400 border-blue-500/30";
    case "contacted":
      return "bg-cyan-500/15 text-cyan-400 border-cyan-500/30";
    case "negotiating":
      return "bg-yellow-500/15 text-yellow-400 border-yellow-500/30";
    case "won":
      return "bg-green-500/15 text-green-400 border-green-500/30";
    case "lost":
      return "bg-red-500/15 text-red-400 border-red-500/30";
    default:
      return "bg-muted text-muted-foreground";
  }
}

export function getStageLabel(stage: string): string {
  switch (stage) {
    case "lead_identified":
      return "Lead Identified";
    case "contacted":
      return "Contacted";
    case "negotiating":
      return "Negotiating";
    case "won":
      return "Won";
    case "lost":
      return "Lost";
    default:
      return stage;
  }
}

export function getPriorityColor(priority: string): string {
  switch (priority) {
    case "high":
      return "text-red-400";
    case "medium":
      return "text-yellow-400";
    case "low":
      return "text-green-400";
    default:
      return "text-muted-foreground";
  }
}

export function displayId(prefix: string, id: number): string {
  return `${prefix}-${String(id).padStart(4, "0")}`;
}
